import React, { useState, useLayoutEffect, useRef, useCallback, useEffect } from 'react';
import {Link, useNavigate, useParams } from 'react-router-dom';
import './TestAgentModal.css';
import rehypeRaw from "rehype-raw";
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import {A2AApiProvider, useA2AApi} from "../Services/A2AApiProvider";
import FileUploader from "./Chat/FileUploader";
import AudioMessageRecorder from "./Chat/AudioMessageRecorder";
import ImageGallery from "./Chat/ImageGallery";
import AudioMessagesPlayer from "./Chat/AudioMessagesPlayer";
import {FileAttachment} from "./Chat/FileAttachment";
import useRealtimeAudioCall from "../Hooks/useRealtimeAudioCall";

// ---- helpers ----
const toPlainJson = (value) =>
  JSON.parse(JSON.stringify(value, (k, v) => {
    if (v instanceof Date) return v.toISOString();
    if (typeof v === 'function') return undefined;
    return v;
  }));

// String/escape-aware extraction of full JSON objects from a text buffer
function extractJsonObjects(text) {
  const out = [];
  let start = -1, depth = 0, inStr = false, esc = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inStr) {
      if (esc) { esc = false; }
      else if (ch === '\\') { esc = true; }
      else if (ch === '"') { inStr = false; }
      continue;
    }

    if (ch === '"') { inStr = true; if (depth === 0 && start === -1) start = i; continue; }
    if (ch === '{') { if (depth === 0) start = i; depth++; continue; }
    if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        const candidate = text.slice(start, i + 1);
        try { out.push(JSON.parse(candidate)); } catch {}
        start = -1;
      }
      continue;
    }
  }
  return out;
}

// tolerant text resolver for mixed shapes
const getMessageText = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;

  // If it's already a "root" TextPart-like shape
  if (item?.kind === 'text') return item?.text || '';

  if (Array.isArray(item?.parts)) {
    return item.parts
      .map(p => {
        const root = p?.root ?? p; // IMPORTANT: supports Part(root=...)
        if (root?.kind === 'text') return root?.text || '';
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }

  return item?.rawText || item?.text || '';
};

const getEventAttachmentsFullFromMetadata = (metadata) => {
  if (!metadata) return [];
  const list = metadata.event_attachments_full;
  if (!Array.isArray(list)) return [];
  return list;
};

const buildAgentArtifactFiles = (metadata) => {
  const eventAttachmentsFull = getEventAttachmentsFullFromMetadata(metadata);
  console.log('eventAttachmentsFull', metadata);

  return eventAttachmentsFull.map((eventAttachment, eventAttachmentIndex) => {
    const displayName = eventAttachment?.display_name || 'file';
    const mime = (eventAttachment?.mime || 'application/octet-stream').toLowerCase();
    const sizeBytes = Number(eventAttachment?.size_bytes ?? 0);
    const fileData = eventAttachment?.file_data || null;
    
    // Extract raw base64 from fileData (might be data URL or raw base64)
    const rawBase64 = extractRawBase64(fileData);
    // Ensure proper data URL for display
    const dataUrl = fileData ? ensureDataUrl(fileData, mime) : null;

    return {
      key: `agent-artifact-${eventAttachmentIndex}-${displayName}`,
      name: displayName,
      type: mime,
      sizeMB: Number.isFinite(sizeBytes) ? (sizeBytes / (1024 * 1024)) : null,
      url: dataUrl,
      base64Preview: dataUrl,
      rawBase64: rawBase64, // Store raw base64 for sending back to backend
      isAgentArtifact: true,
      originalSizeBytes: sizeBytes,
    };
  });
};

// Helper to extract raw base64 from data URL or return as-is if already raw
const extractRawBase64 = (value) => {
  if (!value || typeof value !== 'string') return null;
  // If it's a data URL, extract the base64 portion
  if (value.startsWith('data:')) {
    const commaIdx = value.indexOf(',');
    if (commaIdx !== -1) {
      return value.slice(commaIdx + 1);
    }
  }
  // Already raw base64 or other format - return as-is
  return value;
};

// Helper to ensure we have a proper data URL for display
const ensureDataUrl = (value, mimeType) => {
  if (!value || typeof value !== 'string') return null;
  // Already a data URL
  if (value.startsWith('data:')) return value;
  // Already a http/https URL
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  // Raw base64 - wrap it
  return `data:${mimeType || 'application/octet-stream'};base64,${value}`;
};

// NEW: parse FilePart(FileWithBytes/FileWithUri) from status.message.parts (reveived from backend)
const getMessageParts = (msg) => {
  if (!msg) return [];
  if (Array.isArray(msg?.parts)) return msg.parts;
  if (Array.isArray(msg?.content)) return msg.content;
  return [];
};

const buildAgentPartFiles = (message) => {
  const parts = getMessageParts(message);

  return parts
    .map(p => p?.root ?? p)
    .filter(root => root?.kind === 'file' && root?.file)
    .map((root, idx) => {
      const f = root.file || {};
      const name = f.name || f.filename || f.file_name || 'file';
      const mime = (f.mime_type || f.mimeType || f.type || 'application/octet-stream').toLowerCase();
      const size = f.size;
      const bytes = f.bytes || '';
      const uri = f.uri || f.url || null;
      
      // Extract raw base64 - bytes might already be a data URL from backend
      const rawBase64 = bytes ? extractRawBase64(bytes) : (uri ? extractRawBase64(uri) : null);
      // Build proper data URL for display
      const dataUrl = rawBase64 ? ensureDataUrl(rawBase64, mime) : uri;

      return {
        key: `agent-part-${idx}-${name}`,
        name: name,
        type: mime,
        sizeMB: size,
        url: dataUrl,
        base64Preview: dataUrl,
        rawBase64: rawBase64, // Store raw base64 for sending back to backend
        isAgentArtifact: true,
        originalSizeBytes: null,
      };
    });
};

const TestAgentModal = ({ isOpen, onClose, isPromptRealtime = false}) => {
    const { organizationId, agentId } = useParams();
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const chatBoxRef = useRef(null);
    const navigate = useNavigate();

    const [preuploadedFiles, setPreuploadedFiles] = useState([]);
    const [clearUploader, setClearUploader] = useState(false);
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);
    const [audioMessageSubmitted, setAudioMessageSubmitted] = useState(false);
    const [pendingAudioData, setPendingAudioData] = useState(null);

    const { sendMessage } = useA2AApi();

    // Realtime audio call hook
    const {
        isCallActive,
        isConnecting,
        callStatus,
        transcriptText,
        toggleCall
    } = useRealtimeAudioCall(agentId);

    useEffect(() => {
        const hasText = input.trim() !== '';
        const hasFiles = preuploadedFiles.length > 0;
        setIsButtonEnabled(hasText || hasFiles);
    }, [input, preuploadedFiles]);

    // Add transcript to messages when call ends with transcript
    useEffect(() => {
        if (callStatus === 'idle' && transcriptText) {
            const time = new Date();
            setMessages(prev => [
                ...prev,
                {
                    from: 'assistant',
                    time: formatTime(time),
                    rawTime: time,
                    rawText: transcriptText,
                    isVoiceCall: true
                }
            ]);
        }
    }, [callStatus, transcriptText]);

    const formatTime = (timestamp) => new Date(timestamp || Date.now()).toLocaleTimeString().slice(0, 8);

    const splitFilesByType = (files = []) => {
      return files.reduce(
        (acc, f) => {
          if (!f?.type) {
            acc.files.push(f);
            return acc;
          }

          if (f.type.startsWith('audio/')) {
            acc.audio.push(f);
          } else if (f.type.startsWith('image/')) {
            acc.images.push(f);
          } else {
            acc.files.push(f);
          }

          return acc;
        },
        { audio: [], images: [], files: [] }
      );
    };

    const handleSend = useCallback(async () => {

        if (!isButtonEnabled) return;

        const currentInput = input;
        setInput('');
        setPreuploadedFiles([]);
        setPendingAudioData(null);
        setClearUploader(true);
        setAudioMessageSubmitted(true);

        const userTime = new Date();

        const normalizedFiles = preuploadedFiles.map(f => ({
          sizeMB: f.sizeMB,
          base64Preview: f.base64Preview ?? f.base64File ?? null,
          url: f.url ?? URL.createObjectURL(f.file),
          name: f.name,
          type: f.type,
        }));

        const audio = pendingAudioData
          ? (() => {
              const { audioBinary, histogram = [], playback_time = 0 } =
                pendingAudioData.jsonData ?? {};

              const mimeType = 'audio/mp4';

              return {
                sizeMB: audioBinary ? audioBinary.length / (1024 * 1024) : null,
                base64Preview: audioBinary
                  ? `data:${mimeType};base64,${audioBinary}`
                  : null,
                url: pendingAudioData.jsonData.file,
                name: 'audio',
                type: mimeType,
                histogram: histogram,
                playbackTime: playback_time,
              };
            })()
          : null;

        const allFiles = [
          ...normalizedFiles,
          ...(audio ? [audio] : []),
        ];

        const { images, files } = splitFilesByType(normalizedFiles);

        const userMessage = {
          from: 'user',
          time: formatTime(userTime),
          rawTime: userTime,
          rawText: currentInput,
          audio: audio,
          images: images,
          files: files,
        };

        const processingMessage = { from: 'assistant', rawText: '*processing...*', isLoader: true };

        // Show user msg + loader immediately
        setMessages(prev => [...prev, userMessage, processingMessage]);

        // Include the fresh user message in the conversation we send; sanitize
        const conversation = toPlainJson([...messages, userMessage]);

        try {
            // Streaming callback — called as the agent progresses
            const eventCallback = (eventMessage) => {
                try {
                    // Accept raw strings or objects
                    const candidateEvents = [];
                    if (typeof eventMessage === 'string') {
                        // If provider forwards raw text chunks, try to extract complete JSON objects
                        const objs = extractJsonObjects(eventMessage);
                        if (objs.length)
                            candidateEvents.push(...objs);
                    } else if (eventMessage && typeof eventMessage === 'object') {
                        candidateEvents.push(eventMessage);
                    }

                    for (const ev of candidateEvents) {
                        const statusEnvelope = ev?.result?.status ? ev.result : null;

                        if (statusEnvelope) {
                            if (statusEnvelope.status.state === 'submitted') continue;

                            const isFinalEvent = statusEnvelope.final === true;
                            const isCompletedState = statusEnvelope.status.state === 'completed';

                            let messageText = getMessageText(statusEnvelope.status.message);
                            if (messageText === '' && !ev?.result?.metadata) {
                                if (isFinalEvent || isCompletedState) {
                                    setMessages(prev => prev.filter(m => !m.isLoader));
                                }
                                continue;
                            }

                            const assistantMessageMetadata = ev?.result?.metadata || {};

                            const agentPartFiles = buildAgentPartFiles(statusEnvelope.status.message);

                            const agentArtifactFiles = buildAgentArtifactFiles(assistantMessageMetadata);

                            const mergedAgentFiles = [...agentArtifactFiles, ...agentPartFiles];
                            const separatedAgentArtifacts = splitFilesByType(mergedAgentFiles);

                            if (messageText === '' && !ev?.result?.metadata && agentPartFiles.length === 0) {
                                if (isFinalEvent || isCompletedState) {
                                    setMessages(prev => prev.filter(m => !m.isLoader));
                                }
                                continue;
                            }
                            
                            // if (messageText === '') messageText = `*${statusEnvelope.status.state}*`;

                            const assistantMessage = {
                                from: 'assistant',
                                time: formatTime(statusEnvelope.status.timestamp),
                                rawTime: new Date(statusEnvelope.status.timestamp || Date.now()),
                                rawText: messageText,
                                metadata: assistantMessageMetadata,
                                audio: separatedAgentArtifacts.audio.length > 0 ? separatedAgentArtifacts.audio[0] : null,
                                images: separatedAgentArtifacts.images,
                                files: separatedAgentArtifacts.files,
                            };

                            console.log('Streaming assistant message:', assistantMessage);

                            // Replace loader with partial, then re-append loader to show "still processing"
                            setMessages(prev => {
                                const withoutLoaders = prev.filter(m => !m.isLoader);

                                if (isFinalEvent || isCompletedState) {
                                    return [...withoutLoaders, assistantMessage];
                                }

                                return [...withoutLoaders, assistantMessage, processingMessage];
                            });
                            continue;
                        }

                        // Also accept plain {from, rawText} messages if backend emits them
                        const from = ev?.from || ev?.role;
                        const rawText = getMessageText(ev);
                        if ((from === 'assistant' || from === 'user') && rawText) {
                            setMessages(prev => {
                                const withoutLoaders = prev.filter(m => !m.isLoader);
                                return [...withoutLoaders, { from, rawText, time: ev.time, rawTime: ev.rawTime }, processingMessage];
                            });
                        }
                    }
                } catch {
                    // Ignore malformed chunks silently
                }
            };

            // Await completion — UI keeps updating via eventCallback
            const finalPayload = await sendMessage(agentId, currentInput, allFiles, conversation, eventCallback);

            // Remove any loaders on completion
            setMessages(prev => prev.filter(m => !m.isLoader));

            // If provider returns a final list, append it (idempotent if already streamed)
            if (Array.isArray(finalPayload)) {
                setMessages(prev => [
                    ...prev,
                    ...finalPayload
                        .filter(item => item && (item.from === 'assistant' || item.from === 'user'))
                        .map(item => {
                            const itemFiles = Array.isArray(item.files) ? item.files : [];
                            const { audio, images, files } = splitFilesByType(itemFiles);

                            const agentArtifactFiles = item.from === 'assistant' ? buildAgentArtifactFiles(item.metadata) : [];

                            const agentPartFiles = item.from === 'assistant' ? buildAgentPartFiles(item) : [];

                            const separatedAgentArtifacts = splitFilesByType([...agentArtifactFiles, ...agentPartFiles]);

                            const mergedImages = [...images, ...separatedAgentArtifacts.images];
                            const mergedFiles = [...files, ...separatedAgentArtifacts.files];
                            const mergedAudio = audio.length > 0 ? audio[0] : (separatedAgentArtifacts.audio.length > 0 ? separatedAgentArtifacts.audio[0] : null);

                            const mappedMessage = {
                                from: item.from,
                                time: item.time,
                                rawTime: item.rawTime,
                                rawText: getMessageText(item),
                                metadata: item.metadata,
                                audio: mergedAudio,
                                images: mergedImages,
                                files: mergedFiles
                            };

                            return mappedMessage;
                        }),
                ]);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            // Replace loader with inline error; keep prior streamed content
            setMessages(prev =>
                prev
                    .filter(m => !m.isLoader)
                    .concat([{ from: 'assistant', rawText: `⚠️ Error: ${error?.message || 'Failed to parse response.'}` }])
            );
        }
    }, [input, agentId, sendMessage, messages, preuploadedFiles, pendingAudioData, isButtonEnabled]);


    useLayoutEffect(() => {
        const el = chatBoxRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if (isButtonEnabled) {
            handleSend();
          }
        }
    };

    const handleTextChange = (e) => {
        setInput(e.target.value);
    };

    if (!isOpen) return null;

    return (
        <div className="agent-test-modal-content">
            <div className="chat-box radius-yes border-yes p-2" ref={chatBoxRef}>
                {messages.map((msg, idx) => (
                    <div key={idx}>
                        <div className={`message ${msg.from}`}>
                            <span className="timestamp">{msg.time}</span>
                            <div className='test-message-content'>
                                {(msg.images?.length > 0 || msg.audio || msg.files?.length > 0) && (
                                  <div className="message-bubble user-msg">
                                    {msg.images?.length > 0 && (
                                      <ImageGallery message={msg} />
                                    )}

                                    {msg.audio && (
                                      <AudioMessagesPlayer
                                        audioUrl={msg.audio.url}
                                        histogramData={msg.audio.histogram || []}
                                        playbackTime={msg.audio.playbackTime || 0}
                                      />
                                    )}

                                    {msg.files?.length > 0 && msg.files.map((file) => (
                                      <FileAttachment file={file}/>
                                    ))}
                                  </div>
                                )}
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                    components={{
                                        a: ({ node, ...props }) => (
                                            <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>
                                        ),
                                    }}
                                >
                                    {typeof msg.rawText === 'string' ? msg.rawText : ''}
                                </ReactMarkdown>
                            </div>
                        </div>
                        {msg.metadata?.reasoning_steps?.length > 0 && (
                          <>
                              {msg.metadata.reasoning_steps.map((step, index) => (
                                <div className="metrics" key={`reasoning-step-${index}`}>
                                    <em>{step}</em>
                                </div>
                              ))}
                          </>
                        )}
                        {msg.metadata?.frames?.length > 0 && (
                          <>
                              {msg.metadata.frames.map((frame, index) => (
                                <div className="metrics" key={`frame-${index}-${frame?.frame_key ?? 'na'}`}>
                                    Frame:{' '}
                                    <Link target="_blank" rel="noopener noreferrer" to={`/organizations/${organizationId}/agent-frames/${frame.frame_key}`}>
                                      <strong>{frame.frame_name}</strong>
                                    </Link>
                                </div>
                              ))}
                          </>
                        )}
                        {msg.metadata?.notifications?.length > 0 && (
                        <>
                            {msg.metadata.notifications.map((notification, index) => (
                              <div className="metrics" key={`note-${index}-${notification?.notification_key ?? 'na'}`}>
                                  Notification:{' '}
                                  <Link target="_blank" rel="noopener noreferrer" to={`/organizations/${organizationId}/notifications-hub/${notification.notification_key}`}>
                                    <strong>{notification.notification_name}</strong>
                                  </Link>
                              </div>
                            ))}
                        </>
                        )}
                        {msg.metadata?.connection_objects?.length > 0 && (
                          <>
                            {msg.metadata.connection_objects.map((connection_object, index) => (
                              <div className="metrics" key={`note-${index}-${connection_object?.connection_object_key ?? 'na'}`}>
                                Connection Object:{' '}
                                <Link target="_blank" rel="noopener noreferrer"
                                      to={`/organizations/${organizationId}/connection-objects/${connection_object.connection_object_key}`}>
                                  <strong>{connection_object.connection_object_name}</strong>
                                </Link>
                              </div>
                            ))}
                          </>
                        )}
                        {msg.metadata?.url_annotations?.length > 0 && (
                        <>
                            {msg.metadata.url_annotations.map((annotation, index) => (
                            <div className="metrics" key={`url-${index}-${annotation?.url ?? 'na'}`}>
                                Web:{' '}
                                <strong><a href={annotation.url} target="_blank" rel="noopener noreferrer">{annotation.title}</a></strong>
                            </div>
                            ))}
                        </>
                        )}
                      {msg.metadata?.web_search_queries?.length > 0 && (
                        <>
                            {msg.metadata.web_search_queries.map((q, index) => (
                                <div className="metrics" key={`web-search-${index}`}>
                                    Web search: <em>{q}</em>
                                </div>
                            ))}
                        </>
                      )}
                      {msg.metadata?.file_annotations?.length > 0 && (
                        <>
                            {msg.metadata.file_annotations.map((annotation, index) => (
                            <div className="metrics" key={`file-${index}-${annotation?.file_key ?? 'na'}`}>
                                Document:{' '}
                                <Link target="_blank" rel="noopener noreferrer" to={`/organizations/${organizationId}/documents-hub/${annotation.file_key}`}>
                                <strong>{annotation.original_name}</strong>
                                </Link>
                            </div>
                            ))}
                        </>
                      )}
                      {msg.metadata?.file_search_queries?.length > 0 && (
                        <>
                            {msg.metadata.file_search_queries.map((q, index) => (
                                <div className="metrics" key={`file-search-${index}`}>
                                    File search: <em>{q}</em>
                                </div>
                            ))}
                        </>
                      )}
                      {msg.metadata?.agent_annotations?.length > 0 && (
                        <>
                        {msg.metadata.agent_annotations.map((annotation, index) => (
                          <div className="metrics" key={`agent-${index}-${annotation?.agent_key ?? 'na'}`}>
                              Agent:{' '}
                              <Link target="_blank" rel="noopener noreferrer" to={`/organizations/${organizationId}/agents-hub/${annotation.agent_key}`}>
                                <strong>{annotation.agent_name}</strong>
                              </Link>
                          </div>
                        ))}
                        </>
                      )}
                        {msg.from === 'assistant' && msg.metrics && (
                            <div className="metrics">
                                Response time: <strong>{msg.metrics.responseTime}s</strong><br />
                                User tokens: <strong>{msg.metrics.userTokens}</strong><br />
                                Agent tokens: <strong>{msg.metrics.agentTokens}</strong>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div>
                <div className="text-area-toolbar">
                    <div className="input-area">
                        <div className="request-input">
                            <textarea
                                id="request"
                                rows="2"
                                value={input}
                                onChange={handleTextChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Message..."
                            />
                        </div>
                    </div>

                    <div className="chat-toolbar">
                        <div className="chat-options">
                            <FileUploader
                                isImageUpload={true}
                                multiple={true}
                                maxFileSizeMB={10}
                                maxImageSizeMB={2}
                                allFormatsAllowed={false}
                                allowedFormats={['jpg', 'jpeg', 'png', 'webm', 'heic']}
                                onFilesChange={(files) => {
                                    setPreuploadedFiles(files);
                                }}
                                clearUploader={clearUploader}
                                setClearUploader={setClearUploader}
                            />
                            <FileUploader
                              multiple={false}
                              maxFileSizeMB={10}
                              maxImageSizeMB={2}
                              allFormatsAllowed={false}
                              allowedFormats={['pdf', 'mp4', 'webm', 'mov']}
                              onFilesChange={(files) => {
                                setPreuploadedFiles(files);
                              }}
                              clearUploader={clearUploader}
                              setClearUploader={setClearUploader}
                            />
                            <AudioMessageRecorder
                                onAudioRecorded={({flags, jsonData}) => {
                                    setPendingAudioData({flags, jsonData});
                                    setIsButtonEnabled(true);
                                }}
                                setAudioMessageSubmitted={setAudioMessageSubmitted}
                                audioMessageSubmitted={audioMessageSubmitted}
                            />
                        </div>
                        {isPromptRealtime && (
                            <div className="submit-button-container">
                                <button
                                    className={`call-button ${isCallActive ? 'active' : ''} ${isConnecting ? 'connecting' : ''}`}
                                    onClick={toggleCall}
                                    disabled={isConnecting}
                                    title={isCallActive ? 'End call' : 'Start voice call'}
                                >
                                    {isConnecting ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32">
                                                <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite" />
                                            </circle>
                                        </svg>
                                    ) : isCallActive ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M6 6h12v12H6z"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                    )}
                                </button>
                                <button
                                    className={`submit-button ${isButtonEnabled ? 'active' : ''}`}
                                    onClick={handleSend}
                                    disabled={!isButtonEnabled}
                                >
                                </button>
                            </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default (props) => (
  <A2AApiProvider>
    <TestAgentModal {...props} />
  </A2AApiProvider>
);
